import { createSelector, createSelectorCreator, defaultMemoize } from 'reselect';
import { isEqual } from 'lodash';

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

export const getCurrentUserId = createSelector(
  (state) => state.app.getIn(['currentUser', 'id']), res => res
);

export const getLinkedOrgs = createDeepEqualSelector(
  (state) => state.app.getIn(['currentUser', 'linkedOrgs']).toJS(), res => res
);

export const getBillingAccountPaymentMethods = createDeepEqualSelector(
  (state) => state.app.getIn(['currentUser', 'billingAccs']).toJS(), res => res
);

export const getSelectedProfileId = createSelector(
  (state) => state.app.getIn(['selectedProfile', 'id']), res => res
);

export const linkedOrgsLoading = createSelector(
  (state) => state.app.getIn(['currentUser', 'linkedOrgsLoading']), res => res
);

export const getSelectedProfileOrg = createDeepEqualSelector(
  getSelectedProfileId,
  getLinkedOrgs,
  (selectedProfileId, linkedOrgs) => linkedOrgs.find(org => org.organizationId === selectedProfileId)
);

export const getSelectedProfileBillingAccounts = createDeepEqualSelector(
  getSelectedProfileOrg,
  getBillingAccountPaymentMethods,
  (selectedProfileOrg, billingAccountPaymentMethods) => selectedProfileOrg?.organizationProfile?.billingAccounts?.map(ba => {
    const paymentMethod = billingAccountPaymentMethods?.find(bapm => bapm.billingAccountId === ba.id);
    const userCanAccessBA = selectedProfileOrg?.userGroups?.length > 0 || !!paymentMethod;

    return {
      ...ba,
      userCanAccess: userCanAccessBA,
      organizationName: selectedProfileOrg.organizationName,
      organizationId: selectedProfileOrg.organizationId,
      billingAddress: ba?.addresses.find(a => a.active && a.defaultAddress && a.category === 'BILLING'),
      paymentMethod
    };
  }) || []
);

export const getLinkedOrganizationPinRevalidations = createDeepEqualSelector(
  getLinkedOrgs,
  getBillingAccountPaymentMethods,
  getCurrentUserId,
  (linkedOrgs, billingAccs, currentUserId) => {
    const pinLinkedOrgs = linkedOrgs
      ?.filter(org => org.isAssociatedWithPin)
      ?.filter(org => {
        if (!org?.groups) return true;
        const inAGroup = org.groups.some(group => group.members?.some(m => m.personId === currentUserId));
        return !inAGroup;
      });

    const orgsToRevalidate = billingAccs
      ?.filter(ba => pinLinkedOrgs.some(o => o.organizationId === ba.organizationId))
      ?.filter(ba => ba.pinValidationRequired)
      ?.map(ba => ({
        organizationId: ba.organizationId,
        accountNumber: ba.accountNumber,
        id: ba.id
      }));

    return orgsToRevalidate ?? [];
  }
);

export const getOrgAccess = createDeepEqualSelector(
  getLinkedOrgs,
  getCurrentUserId,
  getBillingAccountPaymentMethods,
  (linkedOrgs, currentUserId, billingAccountPaymentMethods) => linkedOrgs.map(org => {
    const hasLegacyAccess = org.isAssociatedWithPin;
    const pinValidationRequired = billingAccountPaymentMethods?.filter(bapm => bapm.organizationId === org.organizationId).some(ba => ba.pinValidationRequired);
    const groups = org.groups?.filter(group => group.members?.some(m => m.personId === currentUserId)) ?? [];
    const roles = [
      ...new Set(
        groups.map(group => group.roles
          ?.map(r => r.name) ?? [])
          .reduce((x, y) => x.concat(y), [])
      )
    ];
    return {
      organizationId: org.organizationId,
      roles,
      hasLegacyAccess,
      pinValidationRequired
    };
  })
);

export const getSelectedOrgAccess = createDeepEqualSelector(
  getSelectedProfileId,
  getOrgAccess,
  (selectedProfileId, orgAccess) => orgAccess.find(access => access.organizationId === selectedProfileId)
);

export const getOrganizationInvitations = createSelector(
  (state) => state.invitations.getIn(['pendingOrganizationInvites']).toJS(), res => res
);

export const getSelectedOrganizationInvitations = createDeepEqualSelector(
  getSelectedProfileId,
  getOrganizationInvitations,
  (selectedProfileId, { loading, error, data }) => ({
    loading,
    error,
    data: data?.filter(invitation => invitation.lookupKey === selectedProfileId)
  })
);
